import { RudderAnalytics } from "@rudderstack/analytics-js";

import type { TRoom } from "@/features/hotels/types/types";
import type { CabinClass, TripType } from "@/types/enums";
import type { FlightSearchType } from "@/types/types";

const writeKey = import.meta.env.VITE_RAHAL_RUDDERSTACK_WRITE_KEY;
const dataPlaneUrl = import.meta.env.VITE_RAHAL_RUDDERSTACK_DATAPLANE_URL;

interface TypedRudderAnalytics {
  identify(userId: string): void;
  track(
    eventName:
      | CommonTrackingEvents.USER_OPENED_APP
      | CommonTrackingEvents.USER_PRESSED_SEARCH_NAVIGATION_BUTTON
      | CommonTrackingEvents.USER_PRESSED_MY_TRIPS_NAVIGATION_BUTTON
      | CommonTrackingEvents.USER_PRESSED_TRAVELERS_NAVIGATION_BUTTON
      | CommonTrackingEvents.USER_PRESSED_CHAT_NAVIGATION_BUTTON
      | CommonTrackingEvents.USER_DELETED_TRAVELER
      | CommonTrackingEvents.USER_ENTERED_CREATING_TRAVELER
      | CommonTrackingEvents.USER_ENTERED_EDITING_TRAVELER
      | CommonTrackingEvents.USER_SAVED_TRAVELER
      | CommonTrackingEvents.USER_CONTACTED_SUPPORT
      | CommonTrackingEvents.UNEXPECTED_CLIENT_ERROR
      | FlightTrackingEvents.USER_SEARCHED_FOR_MORE_OFFERS
      | FlightTrackingEvents.USER_SEARCHED_FOR_ORDERS
      | FlightTrackingEvents.USER_ENTERED_PARTICULAR_ORDER
      | HotelTrackingEvents.USER_SEARCHED_FOR_MORE_OFFERS
      | HotelTrackingEvents.USER_SEARCHED_FOR_ORDERS
      | HotelTrackingEvents.USER_ENTERED_PARTICULAR_ORDER
      | BnplTrackingEvents.USER_PASSED_ELIGIBILITY_CHECK
      | BnplTrackingEvents.USER_FAILED_ELIGIBILITY_CHECK
      | BnplTrackingEvents.USER_PASSED_BNPL_OTP
      | BnplTrackingEvents.USER_FAILED_BNPL_OTP
      | HotelSearchTrackingEvents.USER_SWITCHED_TO_HOTEL_SEARCH
      | FlightSearchTrackingEvents.USER_SWITCHED_TO_FLIGHT_SEARCH
      | TripTrackingEvents.USER_SWITCHED_TO_TRIPS
      | TripTrackingEvents.USER_VIEWED_TRIP_LIST
      | TripTrackingEvents.USER_SEARCHED_FOR_ORDERS
      | TripTrackingEvents.USER_ENTERED_PARTICULAR_ORDER
      | TripTrackingEvents.TRIP_CAROUSEL_VIEWED,
  ): void;
  track(
    eventName:
      | CommonTrackingEvents.USER_SELECTED_TRAVELER
      | CommonTrackingEvents.USER_DESELECTED_TRAVELER,
    properties: {
      traveler_type: "adult" | "child" | "infant";
      traveler_type_at_travel_date: "adult" | "child" | "infant";
    },
  ): void;
  track(
    eventName: CommonTrackingEvents.USER_LOGGED_IN,
    properties: {
      login_method: string;
    },
  ): void;
  track(
    eventName:
      | CommonTrackingEvents.USER_SCANNED_DOCUMENT_WITH_CAMERA
      | CommonTrackingEvents.USER_UPLOADED_DOCUMENT_SCAN,
    properties: {
      screen: string;
    },
  ): void;
  track(
    eventName:
      | BnplTrackingEvents.USER_SELECTED_BNPL_PAYMENT
      | BnplTrackingEvents.USER_SELECTED_STANDARD_PAYMENT,
    properties: {
      pt_id?: string;
      is_bnpl_eligible: boolean | null;
    },
  ): void;
  track(
    eventName: FlightTrackingEvents.USER_CONTINUED_AFTER_FLIGHT_DETAILS,
    properties: {
      bookingType: "Flight";
      selected_offer_id: string | undefined;
      price: number;
    },
  ): void;
  track(
    eventName: HotelTrackingEvents.USER_PROCEED_AFTER_ROOM_SELECTION,
    properties: {
      bookingType: "Hotel";
      selected_offer_id: string | undefined;
      price: number | undefined;
    },
  ): void;
  track(
    eventName:
      | FlightTrackingEvents.USER_CONTINUED_ON_TRAVELERS
      | HotelTrackingEvents.USER_CONTINUED_ON_TRAVELERS,
    properties: {
      passenger_count: number;
    },
  ): void;
  track(
    eventName: HotelTrackingEvents.USER_SEARCHED_FOR_OFFERS,
    properties: {
      destination: string | undefined;
      travel_date: Date;
      return_date: Date;
      rooms: TRoom[];
    },
  ): void;
  track(
    eventName: FlightTrackingEvents.USER_SEARCHED_FOR_OFFERS,
    properties: {
      tripType: TripType;
      departure_city?: string;
      destination?: string;
      travel_date: Date;
      return_date?: Date;
      travellers: number;
    },
  ): void;
  track(
    eventName:
      | HotelTrackingEvents.USER_PROCEED_TO_PAYMENT_SCREEN
      | FlightTrackingEvents.USER_PROCEED_TO_PAYMENT_SCREEN,
    properties: {
      payment_method: string;
      amount: number | undefined;
      currency: string | undefined;
    },
  ): void;
  track(
    eventName: HotelTrackingEvents.USER_ENTERED_PARTICULAR_OFFER,
    properties: {
      offer_id: string;
      offer_type: "Hotel";
      price: number | undefined;
    },
  ): void;
  track(
    eventName: FlightTrackingEvents.USER_SELECTED_PARTICULAR_FLIGHT,
    properties: {
      offer_id: string;
      offer_type: "Flight";
      price: number | undefined;
    },
  ): void;
  track(
    eventName:
      | FlightTrackingEvents.USER_UNSUCCESSFULY_PAID
      | HotelTrackingEvents.USER_UNSUCCESSFULY_PAID,
    properties: {
      payment_method: string;
      payment_status: "fail";
      transaction_id: string | undefined;
    },
  ): void;
  track(
    eventName: TripTrackingEvents.USER_UNSUCCESSFULY_PAID,
    properties: {
      trip_id: string;
      trip_name: string;
      error_type: string;
      error_message: string;
    },
  ): void;
  track(
    eventName:
      | FlightTrackingEvents.USER_SUCCESSFULY_PAID
      | HotelTrackingEvents.USER_SUCCESSFULY_PAID,
    properties: {
      payment_method: string;
      payment_status: "Success";
      transaction_id: string;
    },
  ): void;
  track(
    eventName: TripTrackingEvents.USER_SUCCESSFULY_PAID,
    properties: {
      trip_id: string;
      trip_name: string;
      order_id: string;
    },
  ): void;
  track(
    eventName:
      | FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_ORIGIN_INPUT
      | FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_DESTINATION_INPUT,
    properties: {
      source: SearchSource | "main_location" | "sticky_location";
    },
  ): void;
  track(
    eventName:
      | FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_CALENDAR_INPUT
      | FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_TRAVELLERS_INPUT
      | HotelSearchTrackingEvents.USER_CLICKED_HOTEL_DESTINATION_INPUT
      | HotelSearchTrackingEvents.USER_CLICKED_HOTEL_CALENDAR_INPUT
      | HotelSearchTrackingEvents.USER_CLICKED_HOTEL_TRAVELLERS_INPUT,
    properties: {
      source: SearchSource;
    },
  ): void;
  track(
    eventName: FlightSearchTrackingEvents.USER_CHANGED_TRIP_TYPE,
    properties: {
      source: SearchSource | "main_calendar" | "sticky_calendar";
      type: TripType;
    },
  ): void;
  track(
    eventName: FlightSearchTrackingEvents.USER_SELECTED_ORIGIN_RECOMMENDATION,
    properties: {
      city_id: string;
      city_name: string;
      source: SearchSource;
    },
  ): void;
  track(
    eventName:
      | FlightSearchTrackingEvents.USER_SELECTED_RECENT_ORIGIN
      | FlightSearchTrackingEvents.USER_SELECTED_RECENT_DESTINATION
      | FlightSearchTrackingEvents.USER_SELECTED_TOP_ORIGIN
      | FlightSearchTrackingEvents.USER_SELECTED_TOP_DESTINATION
      | FlightSearchTrackingEvents.USER_SELECTED_SEARCH_ORIGIN
      | FlightSearchTrackingEvents.USER_SELECTED_SEARCH_DESTINATION
      | HotelSearchTrackingEvents.USER_SELECTED_HOTEL_RECENT_DESTINATION
      | HotelSearchTrackingEvents.USER_SELECTED_HOTEL_TOP_DESTINATION,
    properties: LocationData & {
      source: SearchSource;
    },
  ): void;
  track(
    eventName: FlightSearchTrackingEvents.USER_CHANGED_CALENDAR_DATE,
    properties: {
      type: TripType;
      dateFrom: string;
      dateTo?: string;
      source: SearchSource;
    },
  ): void;
  track(
    eventName: HotelSearchTrackingEvents.USER_CHANGED_HOTEL_CALENDAR_DATE,
    properties: {
      dateFrom: string;
      dateTo: string;
      source: SearchSource;
    },
  ): void;
  track(
    eventName: FlightSearchTrackingEvents.USER_CHANGED_TRAVELLERS_AND_CLASS,
    properties: {
      travellers: {
        adults: number;
        children: number;
        infants: number;
      };
      class: CabinClass;
      source: SearchSource;
    },
  ): void;
  track(
    eventName: HotelSearchTrackingEvents.USER_CHANGED_HOTEL_TRAVELLERS,
    properties: {
      travellers: {
        adults: number;
        children: number;
        childrenAges: (string | number)[];
      };
      source: SearchSource;
    },
  ): void;
  track(
    eventName: FlightTrackingEvents.NO_FLIGHT_RESULTS_FOUND,
    properties: {
      originId: string;
      originName: string;
      destinationId: string;
      destinationName: string;
      dateFrom: string;
      dateTo?: string;
      travellers: number;
      cabinClass: CabinClass;
      source: FlightSearchType;
    },
  ): void;
  track(
    eventName: HotelTrackingEvents.NO_HOTEL_RESULTS_FOUND,
    properties: {
      destinationId: string;
      destinationName: string;
      dateFrom: string;
      dateTo: string;
      travellers: number;
    },
  ): void;
  track(
    eventName: CommonTrackingEvents.USER_REJECTED_CONSENT,
    properties: {
      scope: "credit_card_information" | "personal_information";
    },
  ): void;
  track(
    eventName:
      | TripTrackingEvents.USER_VIEWED_TRIP_DETAILS
      | TripTrackingEvents.USER_PROCEEDED_TO_TRIP_AVAILABILITY
      | TripTrackingEvents.USER_PROCEEDED_TO_TRIP_TRAVELLERS
      | TripTrackingEvents.TRIP_CAROUSEL_CLICKED,
    properties: {
      trip_id: string;
      trip_name: string;
    },
  ): void;
  track(
    eventName: TripTrackingEvents.USER_ADDED_TRAVELLER,
    properties: {
      trip_id: string;
      trip_name: string;
      adult_count: number;
      child_count: number;
      infant_count: number;
    },
  ): void;
  track(
    eventName: TripTrackingEvents.USER_REVIEWED_TRIP_SUMMARY,
    properties: {
      trip_id: string;
      trip_name: string;
      total_price: number;
    },
  ): void;
  track(
    eventName:
      | TripTrackingEvents.USER_VIEWED_TRIP_OPTIONAL_TOURS
      | TripTrackingEvents.USER_ADDED_TRIP_OPTIONAL_TOURS
      | TripTrackingEvents.USER_REMOVED_TRIP_OPTIONAL_TOURS,
    properties: {
      source: "details" | "auto" | "summary";
      trip_id: string;
      trip_name: string;
      price: number;
    },
  ): void;
  track(
    eventName: TripTrackingEvents.UNEXPECTED_CLIENT_ERROR,
    properties: {
      trip_id: string;
      error_type: string;
      error_message: string;
    },
  ): void;
  track(
    eventName: ContentTrackingEvents.CONTENT_PLACEMENT_VIEWED,
    properties: {
      placement: string;
      banner_count: number;
    },
  ): void;
  track(
    eventName: ContentTrackingEvents.CONTENT_BANNER_CLICKED,
    properties: {
      placement: string;
      banner_name: string;
      banner_image_url: string;
      banner_position: number;
    },
  ): void;
  track(
    eventName: ContentTrackingEvents.CONTENT_BLOCK_BUTTON_CLICKED,
    properties: {
      placement: string;
      banner_name: string;
      content_block: string;
      button_text: string;
    },
  ): void;
  track(
    eventName:
      | TripTrackingEvents.TRIP_PROMO_BANNER_VIEWED
      | TripTrackingEvents.TRIP_PROMO_BANNER_CLICKED,
    properties: {
      package_id: string;
      package_name: string;
      banner_name: string;
      banner_url: string;
      list: "flight" | "hotel";
      city_name: string;
      city_id: string;
      position: number;
    },
  ): void;
}

type LocationData = {
  location_id: string;
  location_name: string;
  location_type: string;
};

export type SearchSource = "main" | "sticky";

const rudderAnalyticsBase = new RudderAnalytics();
if (writeKey && dataPlaneUrl) {
  rudderAnalyticsBase.load(writeKey, dataPlaneUrl, {});
}

export enum CommonTrackingEvents {
  USER_OPENED_APP = "USER_OPENED_APP",
  USER_LOGGED_IN = "USER_LOGGED_IN",
  USER_PRESSED_SEARCH_NAVIGATION_BUTTON = "USER_PRESSED_SEARCH_NAVIGATION_BUTTON",
  USER_PRESSED_MY_TRIPS_NAVIGATION_BUTTON = "USER_PRESSED_MY_TRIPS_NAVIGATION_BUTTON",
  USER_PRESSED_TRAVELERS_NAVIGATION_BUTTON = "USER_PRESSED_TRAVELERS_NAVIGATION_BUTTON",
  USER_PRESSED_CHAT_NAVIGATION_BUTTON = "USER_PRESSED_CHAT_NAVIGATION_BUTTON",
  USER_SELECTED_TRAVELER = "USER_SELECTED_TRAVELER",
  USER_DESELECTED_TRAVELER = "USER_DESELECTED_TRAVELER",
  USER_DELETED_TRAVELER = "USER_DELETED_TRAVELER",
  USER_ENTERED_CREATING_TRAVELER = "USER_ENTERED_CREATING_TRAVELER",
  USER_ENTERED_EDITING_TRAVELER = "USER_ENTERED_EDITING_TRAVELER",
  USER_SCANNED_DOCUMENT_WITH_CAMERA = "USER_SCANNED_DOCUMENT_WITH_CAMERA",
  USER_UPLOADED_DOCUMENT_SCAN = "USER_UPLOADED_DOCUMENT_SCAN",
  USER_SAVED_TRAVELER = "USER_SAVED_TRAVELER",
  USER_SUCCESSFULY_PAID_FOR_HOTEL = "USER_SUCCESSFULY_PAID_FOR_HOTEL",
  USER_UNSUCCESSFULY_PAID_FOR_HOTEL = "USER_UNSUCCESSFULY_PAID_FOR_HOTEL",
  USER_CONTACTED_SUPPORT = "USER_CONTACTED_SUPPORT",
  UNEXPECTED_CLIENT_ERROR = "UNEXPECTED_CLIENT_ERROR",
  USER_REJECTED_CONSENT = "USER_REJECTED_CONSENT",
}

export enum FlightTrackingEvents {
  USER_SEARCHED_FOR_OFFERS = "USER_SEARCHED_FOR_FLIGHT_OFFERS",
  USER_SEARCHED_FOR_MORE_OFFERS = "USER_SEARCHED_FOR_MORE_FLIGHT_OFFERS",
  USER_SELECTED_PARTICULAR_FLIGHT = "USER_SELECTED_PARTICULAR_FLIGHT",
  USER_CONTINUED_AFTER_FLIGHT_DETAILS = "USER_CONTINUED_AFTER_FLIGHT_DETAILS",
  USER_CONTINUED_ON_TRAVELERS = "USER_CONTINUED_ON_FLIGHT_TRAVELERS",
  USER_PROCEED_TO_PAYMENT_SCREEN = "USER_PROCEED_TO_FLIGHT_PAYMENT_SCREEN",
  USER_SUCCESSFULY_PAID = "USER_SUCCESSFULY_PAID_FOR_FLIGHT",
  USER_UNSUCCESSFULY_PAID = "USER_UNSUCCESSFULY_PAID_FOR_FLIGHT",
  USER_SEARCHED_FOR_ORDERS = "USER_SEARCHED_FOR_FLIGHT_ORDERS",
  USER_ENTERED_PARTICULAR_ORDER = "USER_ENTERED_PARTICULAR_FLIGHT_ORDER",
  NO_FLIGHT_RESULTS_FOUND = "NO_FLIGHT_RESULTS_FOUND",
}

export enum HotelTrackingEvents {
  USER_SEARCHED_FOR_OFFERS = "USER_SEARCHED_FOR_HOTEL_OFFERS",
  USER_SEARCHED_FOR_MORE_OFFERS = "USER_SEARCHED_FOR_MORE_HOTEL_OFFERS",
  USER_ENTERED_PARTICULAR_OFFER = "USER_ENTERED_PARTICULAR_HOTEL_OFFER",
  USER_PROCEED_AFTER_ROOM_SELECTION = "USER_PROCEED_AFTER_ROOM_SELECTION",
  USER_CONTINUED_ON_TRAVELERS = "USER_CONTINUED_ON_HOTEL_TRAVELERS",
  USER_PROCEED_TO_PAYMENT_SCREEN = "USER_PROCEED_TO_HOTEL_PAYMENT_SCREEN",
  USER_SUCCESSFULY_PAID = "USER_SUCCESSFULY_PAID_FOR_HOTEL",
  USER_UNSUCCESSFULY_PAID = "USER_UNSUCCESSFULY_PAID_FOR_HOTEL",
  USER_SEARCHED_FOR_ORDERS = "USER_SEARCHED_FOR_HOTEL_ORDERS",
  USER_ENTERED_PARTICULAR_ORDER = "USER_ENTERED_PARTICULAR_HOTEL_ORDER",
  NO_HOTEL_RESULTS_FOUND = "NO_HOTEL_RESULTS_FOUND",
}

export enum BnplTrackingEvents {
  USER_SELECTED_BNPL_PAYMENT = "USER_SELECTED_BNPL_PAYMENT",
  USER_SELECTED_STANDARD_PAYMENT = "USER_SELECTED_STANDARD_PAYMENT",
  USER_PASSED_ELIGIBILITY_CHECK = "USER_PASSED_ELIGIBILITY_CHECK",
  USER_FAILED_ELIGIBILITY_CHECK = "USER_FAILED_ELIGIBILITY_CHECK",
  USER_PASSED_BNPL_OTP = "USER_PASSED_BNPL_OTP",
  USER_FAILED_BNPL_OTP = "USER_FAILED_BNPL_OTP",
}

export enum HotelSearchTrackingEvents {
  USER_SWITCHED_TO_HOTEL_SEARCH = "USER_SWITCHED_TO_HOTEL_SEARCH",
  USER_CLICKED_HOTEL_DESTINATION_INPUT = "USER_CLICKED_HOTEL_DESTINATION_INPUT",
  USER_CLICKED_HOTEL_CALENDAR_INPUT = "USER_CLICKED_HOTEL_CALENDAR_INPUT",
  USER_CLICKED_HOTEL_TRAVELLERS_INPUT = "USER_CLICKED_HOTEL_TRAVELLERS_INPUT",
  USER_SELECTED_HOTEL_RECENT_DESTINATION = "USER_SELECTED_HOTEL_RECENT_DESTINATION",
  USER_SELECTED_HOTEL_TOP_DESTINATION = "USER_SELECTED_HOTEL_TOP_DESTINATION",
  USER_CHANGED_HOTEL_CALENDAR_DATE = "USER_CHANGED_HOTEL_CALENDAR_DATE",
  USER_CHANGED_HOTEL_TRAVELLERS = "USER_CHANGED_HOTEL_TRAVELLERS",
}

export enum FlightSearchTrackingEvents {
  USER_SWITCHED_TO_FLIGHT_SEARCH = "USER_SWITCHED_TO_FLIGHT_SEARCH",
  USER_CLICKED_FLIGHT_ORIGIN_INPUT = "USER_CLICKED_FLIGHT_ORIGIN_INPUT",
  USER_CLICKED_FLIGHT_DESTINATION_INPUT = "USER_CLICKED_FLIGHT_DESTINATION_INPUT",
  USER_CLICKED_FLIGHT_CALENDAR_INPUT = "USER_CLICKED_FLIGHT_CALENDAR_INPUT",
  USER_CLICKED_FLIGHT_TRAVELLERS_INPUT = "USER_CLICKED_FLIGHT_TRAVELLERS_INPUT",
  USER_CHANGED_TRIP_TYPE = "USER_CHANGED_TRIP_TYPE",
  USER_SELECTED_ORIGIN_RECOMMENDATION = "USER_SELECTED_ORIGIN_RECOMMENDATION",
  USER_SELECTED_RECENT_ORIGIN = "USER_SELECTED_RECENT_ORIGIN",
  USER_SELECTED_RECENT_DESTINATION = "USER_SELECTED_RECENT_DESTINATION",
  USER_SELECTED_TOP_ORIGIN = "USER_SELECTED_TOP_ORIGIN",
  USER_SELECTED_TOP_DESTINATION = "USER_SELECTED_TOP_DESTINATION",
  USER_SELECTED_SEARCH_ORIGIN = "USER_SELECTED_SEARCH_ORIGIN",
  USER_SELECTED_SEARCH_DESTINATION = "USER_SELECTED_SEARCH_DESTINATION",
  USER_CHANGED_CALENDAR_DATE = "USER_CHANGED_CALENDAR_DATE",
  USER_CHANGED_TRAVELLERS_AND_CLASS = "USER_CHANGED_TRAVELLERS_AND_CLASS",
}

export enum TripTrackingEvents {
  USER_SWITCHED_TO_TRIPS = "USER_SWITCHED_TO_TRIPS",
  USER_VIEWED_TRIP_LIST = "USER_VIEWED_TRIP_LIST",
  USER_VIEWED_TRIP_DETAILS = "USER_VIEWED_TRIP_DETAILS",
  USER_PROCEEDED_TO_TRIP_AVAILABILITY = "USER_PROCEEDED_TO_TRIP_AVAILABILITY",
  USER_PROCEEDED_TO_TRIP_TRAVELLERS = "USER_PROCEEDED_TO_TRIP_TRAVELLERS",
  USER_ADDED_TRAVELLER = "USER_ADDED_TRAVELLER",
  USER_REVIEWED_TRIP_SUMMARY = "USER_REVIEWED_TRIP_SUMMARY",
  USER_SUCCESSFULY_PAID = "USER_SUCCESSFULY_PAID_FOR_TRIP",
  USER_UNSUCCESSFULY_PAID = "USER_UNSUCCESSFULY_PAID_FOR_TRIP",
  UNEXPECTED_CLIENT_ERROR = "UNEXPECTED_CLIENT_ERROR",
  USER_SEARCHED_FOR_ORDERS = "USER_SEARCHED_FOR_HOTEL_ORDERS",
  USER_ENTERED_PARTICULAR_ORDER = "USER_ENTERED_PARTICULAR_TRIP_ORDER",
  TRIP_CAROUSEL_VIEWED = "TRIP_CAROUSEL_VIEWED",
  TRIP_CAROUSEL_CLICKED = "TRIP_CAROUSEL_CLICKED",
  USER_VIEWED_TRIP_OPTIONAL_TOURS = "USER_VIEWED_TRIP_OPTIONAL_TOURS",
  USER_ADDED_TRIP_OPTIONAL_TOURS = "USER_ADDED_TRIP_OPTIONAL_TOURS",
  USER_REMOVED_TRIP_OPTIONAL_TOURS = "USER_REMOVED_TRIP_OPTIONAL_TOURS",
  TRIP_PROMO_BANNER_VIEWED = "TRIP_PROMO_BANNER_VIEWED",
  TRIP_PROMO_BANNER_CLICKED = "TRIP_PROMO_BANNER_CLICKED",
}

export enum ContentTrackingEvents {
  CONTENT_PLACEMENT_VIEWED = "CONTENT_PLACEMENT_VIEWED",
  CONTENT_BANNER_CLICKED = "CONTENT_BANNER_CLICKED",
  CONTENT_BLOCK_BUTTON_CLICKED = "CONTENT_BLOCK_BUTTON_CLICKED",
}

export const rudderAnalytics: TypedRudderAnalytics = rudderAnalyticsBase;
